/**
 * ModernEventsTab - Enhanced events management with modern cards and forms
 */
import React, { useState } from 'react';
import { 
  Calendar, Users, Clock, MapPin, DollarSign, Plus, Pencil, Trash2, 
  Utensils, Music, Image as ImageIcon, Eye, TrendingUp, Filter 
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAdmin } from './AdminHooks';
import { cn } from '../../lib/utils';

const EventStatusBadge = ({ status }) => {
  const statusConfig = {
    past: { label: 'Terminé', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
    upcoming: { label: 'À venir', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    available: { label: 'Disponible', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
    sold_out: { label: 'Complet', className: 'bg-red-500/20 text-red-400 border-red-500/30' }
  };

  const config = statusConfig[status] || { label: status, className: 'bg-gray-500/20 text-gray-400' };

  return (
    <Badge className={cn(config.className)}>
      {config.label}
    </Badge>
  );
};

const IconChip = ({ children }) => (
  <span className="inline-flex justify-center items-center w-7 h-7 rounded-md text-slate-300">
    {children}
  </span>
);

/* Stats chips removed per request */

const EventCard = ({ event, onEdit, onDelete, onMenu, onLineup, onMedia, onPreview }) => (
  <Card className={cn(
    "bg-slate-900/40 backdrop-blur-md border-slate-700 hover:bg-slate-900/60 transition-all duration-300 hover:shadow-xl overflow-hidden",
    event.status === 'past' && "opacity-60"
  )}>
    <div className={cn(
      "h-2 bg-gradient-to-r",
      event.status === 'past' && "from-slate-600 to-slate-700",
      event.status === 'upcoming' && "from-blue-600 to-cyan-600",
      event.status === 'available' && "from-green-600 to-emerald-600",
      event.status === 'sold_out' && "from-red-600 to-orange-600"
    )} />
    
    <CardHeader className="pb-4">
      <div className="flex justify-between items-start mb-2">
        <div className="flex gap-2 items-center">
          <span className="text-xs font-bold tracking-wider uppercase text-slate-400">
            Vol.{event.volume}
          </span>
          <EventStatusBadge status={event.status} />
          {event.reservationsOpen && (
            <Badge className="text-green-400 bg-green-500/20 border-green-500/30">
              Réservations ouvertes
            </Badge>
          )}
        </div>
      </div>
      
      <CardTitle className="mb-2 text-xl font-bold text-white">
        {event.theme}
      </CardTitle>
      
      <CardDescription className="text-slate-400">
        {event.description || 'Aucune description disponible'}
      </CardDescription>

      <div className="flex gap-1 justify-end pt-3 mt-3 border-t border-slate-700">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onEdit(event)}
          className="text-slate-400 hover:text-white hover:bg-slate-700"
          aria-label="Modifier"
        >
          <Pencil size={14} />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDelete(event.id)}
          className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
          aria-label="Supprimer"
        >
          <Trash2 size={14} />
        </Button>
      </div>
    </CardHeader>
    
    <CardContent className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <Badge className="inline-flex gap-1 items-center text-white bg-white/10 border-white/20">
          <Calendar className="w-3.5 h-3.5" />
          {event.date}
        </Badge>
        <Badge className="inline-flex gap-1 items-center text-white bg-white/10 border-white/20">
          <Clock className="w-3.5 h-3.5" />
          {event.time}
        </Badge>
        <Badge className="inline-flex gap-1 items-center text-white bg-white/10 border-white/20">
          <DollarSign className="w-3.5 h-3.5" />
          {event.price}€
        </Badge>
        <Badge className="inline-flex gap-1 items-center text-white bg-white/10 border-white/20">
          <Users className="w-3.5 h-3.5" />
          {event.capacity || 'Illimité'} places
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 gap-2 pt-4 border-t sm:grid-cols-4 border-slate-700">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onMenu(event)}
          className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
          aria-label="Menu"
        >
          <Utensils size={16} />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onLineup(event)}
          className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
          aria-label="Line-up"
        >
          <Music size={16} />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onMedia(event)}
          className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
          aria-label="Media"
        >
          <ImageIcon size={16} />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
          onClick={() => onPreview(event)}
          aria-label="Aperçu"
        >
          <Eye size={16} />
        </Button>
      </div>
    </CardContent>
  </Card>
);

const ModernEventsTab = () => {
  const {
    events, loading, openEventModal, handleDeleteEvent, 
    openMenuModal, openLineupModal, openMediaModal
  } = useAdmin();

  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [previewEvent, setPreviewEvent] = useState(null);

  const filteredEvents = React.useMemo(() => {
    if (!events) return [];
    
    let filtered = events;
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(event => event.status === filterStatus);
    }
    
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(a.date) - new Date(b.date);
        case 'volume':
          return a.volume - b.volume;
        case 'price':
          return a.price - b.price;
        default:
          return 0;
      }
    });
  }, [events, filterStatus, sortBy]);

  const stats = React.useMemo(() => {
    if (!events) return { total: 0, upcoming: 0, past: 0, soldOut: 0 };
    
    return {
      total: events.length,
      upcoming: events.filter(e => e.status === 'upcoming').length,
      past: events.filter(e => e.status === 'past').length,
      soldOut: events.filter(e => e.status === 'sold_out').length
    };
  }, [events]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestion des Événements</h2>
          <p className="text-slate-400">
            {filteredEvents.length} événement(s) trouvé(s)
          </p>
        </div>
        <Button
          onClick={() => openEventModal()}
          className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
        >
          <Plus size={16} className="mr-2" />
          Nouvel Événement
        </Button>
      </div>

      <div className="flex overflow-x-auto flex-nowrap gap-2 items-center">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px] bg-slate-900 border-slate-600 text-white">
            <Filter size={16} className="mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-600">
            <SelectItem value="all" className="text-white">Status</SelectItem>
            <SelectItem value="upcoming" className="text-white">À venir</SelectItem>
            <SelectItem value="available" className="text-white">Disponible</SelectItem>
            <SelectItem value="sold_out" className="text-white">Complet</SelectItem>
            <SelectItem value="past" className="text-white">Passé</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[150px] bg-slate-900 border-slate-600 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-600">
            <SelectItem value="date" className="text-white">Date</SelectItem>
            <SelectItem value="volume" className="text-white">Volume</SelectItem>
            <SelectItem value="price" className="text-white">Prix</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Events Grid */}
      {loading ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8 text-center">
            <div className="mx-auto mb-4 w-8 h-8 rounded-full border-b-2 border-blue-400 animate-spin"></div>
            <p className="text-slate-400">Chargement des événements...</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredEvents.length === 0 ? (
            <Card className="col-span-full bg-slate-800/50 border-slate-700">
              <CardContent className="p-8 text-center">
                <Calendar size={48} className="mx-auto mb-4 text-slate-600" />
                <p className="text-slate-400">Aucun événement trouvé</p>
              </CardContent>
            </Card>
          ) : (
            filteredEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onEdit={openEventModal}
                onDelete={handleDeleteEvent}
                onMenu={openMenuModal}
                onLineup={openLineupModal}
                onMedia={openMediaModal}
                onPreview={setPreviewEvent}
              />
            ))
          )}
        </div>
      )}
      <Dialog open={!!previewEvent} onOpenChange={(o) => !o && setPreviewEvent(null)}>
        <DialogContent className="max-w-2xl text-white bg-slate-950 border-slate-800">
          <DialogHeader>
            <DialogTitle>Aperçu de l'événement</DialogTitle>
            <DialogDescription>
              Prévisualisation des informations principales de l&rsquo;événement.
            </DialogDescription>
          </DialogHeader>
          {previewEvent && (
            <div className="overflow-hidden relative p-6 bg-gradient-to-br from-gray-900 rounded-lg border to-gray-950 border-white/20">
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl bg-white/5" />
              <div className="relative">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span className="text-xs tracking-widest text-gray-500 uppercase">Vol.{previewEvent.volume}</span>
                    <h4 className="mt-1 text-2xl font-bold">{previewEvent.theme}</h4>
                  </div>
                  <span className="px-3 py-1 text-xs tracking-wider text-white uppercase rounded-full bg-white/10">
                    {previewEvent.status === 'available' ? 'Ouvert' : previewEvent.status === 'sold_out' ? 'Complet' : 'Bientôt'}
                  </span>
                </div>
                <div className="flex gap-4 items-center mb-6 text-sm text-gray-300">
                  <div className="flex gap-1 items-center">
                    <Calendar size={14} />
                    <span>{previewEvent.date}</span>
                  </div>
                  <div className="flex gap-1 items-center">
                    <Clock size={14} />
                    <span>{previewEvent.time}</span>
                  </div>
                </div>
                {previewEvent.description && (
                  <p className="mb-6 text-gray-400" style={{ whiteSpace: 'pre-line' }}>{previewEvent.description}</p>
                )}
                <div className="flex justify-between items-center pt-4 border-t border-white/10">
                  {previewEvent.reservationsOpen ? (
                    <button className="px-6 py-2 text-sm font-semibold tracking-wider text-black uppercase bg-white rounded-full transition-colors hover:bg-gray-200">
                      Réserver
                    </button>
                  ) : (
                    <span className="text-sm text-gray-500">Réservations bientôt ouvertes</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

ModernEventsTab.propTypes = {};

export default ModernEventsTab;
